// The daterangepicker widget draws its own calendar chrome (month headings, weekday
// row, preset list, Apply/Cancel) from strings it is handed, so the portal's language
// has to be passed in explicitly — otherwise an Arabic page opens an English calendar.
// The widget also *parses* the trigger input by splitting it on `separatorLabel` and
// reading each half with `format`, so the moment pattern and the React-rendered value
// must come from the same rule or the end date lands on "Invalid date".
//
// Only the field ORDER is taken from Intl; the digits and separators stay ASCII, so a
// ledger never renders bidi marks or Arabic-Indic numerals inside a numeric column.
const TOKENS = { day: "DD", month: "MM", year: "YYYY" };
const opts = { day: "2-digit", month: "2-digit", year: "numeric" };

// daterangepicker's grid starts at `firstDay`. Intl.Locale#weekInfo carries the CLDR
// value but is absent in Chromium, where every locale would silently fall back to
// Sunday — Saturday for Arabic and Monday for Spanish are the visible differences, so
// the shipped languages pin their CLDR value (0=Sun) and weekInfo covers the rest.
const FIRST_DAY = { en: 0, es: 1, hi: 0, ar: 6 };
const firstDayOf = (languageCode) => {
  if (languageCode in FIRST_DAY) return FIRST_DAY[languageCode];
  try {
    return (new Intl.Locale(languageCode || "en").weekInfo?.firstDay ?? 7) % 7;
  } catch {
    return 0;
  }
};
// daterangepicker 3.1.0 tints a cell "weekend" from a hardcoded isoWeekday() > 5 test and
// has no locale option for it, so an Arabic grid marks Sunday along with Saturday. Fixing
// that needs a widget upgrade, not a config key.

const fieldOrder = (languageCode) => {
  const parts = new Intl.DateTimeFormat(`${languageCode || "en"}-u-nu-latn`, opts)
    .formatToParts(new Date(2024, 0, 2))
    .filter((p) => p.type in TOKENS)
    .map((p) => p.type);
  return parts.length === 3 ? parts : ["month", "day", "year"];
};

export const pickerFormat = (languageCode) =>
  fieldOrder(languageCode)
    .map((t) => TOKENS[t])
    .join("/");

export const pickerDate = (value, languageCode) => {
  // A bare "YYYY-MM-DD" string parses as UTC midnight and shifts a day back for
  // viewers west of Greenwich, so it is split into local date parts first.
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ""));
  const d = iso
    ? new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))
    : value instanceof Date
    ? value
    : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  const num = { day: pad(d.getDate()), month: pad(d.getMonth() + 1), year: String(d.getFullYear()) };
  return fieldOrder(languageCode)
    .map((t) => num[t])
    .join("/");
};

export const pickerLocale = (languageCode, t) => {
  const locale = `${languageCode || "en"}-u-nu-latn`;
  // 2024-01-07 is a Sunday, so 7..13 walk the week in the order the grid expects.
  const days = new Intl.DateTimeFormat(locale, { weekday: "short" });
  const months = new Intl.DateTimeFormat(locale, { month: "long" });
  return {
    format: pickerFormat(languageCode),
    separatorLabel: " - ",
    applyLabel: t("date_range_apply", "Apply"),
    cancelLabel: t("date_range_cancel", "Cancel"),
    todayLabel: t("today", "Today"),
    customRangeLabel: t("date_range_custom_range", "Custom Range"),
    daysOfWeek: [7, 8, 9, 10, 11, 12, 13].map((d) => days.format(new Date(2024, 0, d))),
    monthNames: Array.from({ length: 12 }, (_, m) => months.format(new Date(2024, m, 1))),
    firstDay: firstDayOf(languageCode),
  };
};
